import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { MapPin, X } from "lucide-react";
import { loadGoogleMaps, isGoogleMapsConfigured } from "../../utils/googleMaps";
import {
  doesPolygonSelfIntersect,
  isDuplicatePoint,
  canAddEventZonePoint,
  MAX_EVENT_ZONE_POINTS,
} from "../../utils/eventZone";

/**
 * Google-Maps-based location picker, ported from the mobile app's
 * map-handler screen (src/Screens/map-handler/index.js) + event-zone-editor.
 * Assumes `mode` is fixed for the component's lifetime — the parent remounts
 * (via `key`) when switching between steps, matching how mobile swaps whole
 * screens rather than live-switching editor modes.
 *
 * mode="pick"    value: {latitude, longitude, address, city, country, radius} | null
 * mode="checkIn" value: [{id, name, latitude, longitude, radius}]
 * mode="zone"    value: [{latitude, longitude, order}]
 */

const DEFAULT_CENTER = { latitude: 52.520008, longitude: 13.404954 }; // Berlin, mirrors mobile's fallback
const MIN_RADIUS = 5;
const RADIUS_STEP = 5;
const DEFAULT_LOCATION_RADIUS = 25;
const DEFAULT_CHECKIN_RADIUS = 5;

let _checkInIdCounter = 0;
const nextCheckInId = () => `checkin-${Date.now()}-${++_checkInIdCounter}`;

const reverseGeocode = (geocoder, latLng) =>
  new Promise((resolve) => {
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status !== "OK" || !results?.length) {
        resolve({ address: "", city: "", country: "" });
        return;
      }
      const result = results[0];
      const components = result.address_components || [];
      const find = (type) => components.find((c) => c.types.includes(type))?.long_name || "";
      resolve({
        address: result.formatted_address || "",
        city: find("locality") || find("postal_town") || find("administrative_area_level_2") || "",
        country: find("country") || "",
      });
    });
  });

const LocationMapPicker = ({ mode = "pick", value, onChange, center, height = 320 }) => {
  const { t } = useTranslation("events");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const pointOverlaysRef = useRef(new Map()); // checkIn: id -> {marker, circle}
  const zoneMarkersRef = useRef([]);
  const zonePolygonRef = useRef(null);
  const zonePointsRef = useRef([]); // kept in sync for click handler closures
  const autocompleteRef = useRef(null);

  const [status, setStatus] = useState(isGoogleMapsConfigured() ? "loading" : "unconfigured");
  const [isLocating, setIsLocating] = useState(false);
  const [zonePoints, setZonePoints] = useState(Array.isArray(value) && mode === "zone" ? value : []);

  const effectiveCenter = useMemo(() => {
    if (mode === "pick" && value?.latitude && value?.longitude) return value;
    if (center?.latitude && center?.longitude) return center;
    return DEFAULT_CENTER;
  }, [mode, value, center]);

  // ---- keep a ref mirror of zonePoints for stable closures in map click handler ----
  useEffect(() => {
    zonePointsRef.current = zonePoints;
  }, [zonePoints]);

  // ---- refs mirroring the latest `value`/`onChange` so imperative Google Maps
  // listeners (attached once, on mount) never act on stale closures. ----
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
  });

  // ---- load script + init map (runs once) ----
  useEffect(() => {
    if (!isGoogleMapsConfigured()) {
      setStatus("unconfigured");
      return;
    }

    let cancelled = false;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !containerRef.current) return;
        geocoderRef.current = new google.maps.Geocoder();
        const map = new google.maps.Map(containerRef.current, {
          center: { lat: effectiveCenter.latitude, lng: effectiveCenter.longitude },
          zoom: mode === "zone" || mode === "checkIn" ? 17 : 15,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        });
        mapRef.current = map;
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================================================================
  // MODE: pick
  // =========================================================================
  const applyPickValue = useCallback((next) => onChange?.(next), [onChange]);

  const setMarkerAndCircle = useCallback((google, map, lat, lng, radius) => {
    if (!markerRef.current) {
      markerRef.current = new google.maps.Marker({
        map,
        position: { lat, lng },
        draggable: true,
      });
      markerRef.current.addListener("dragend", (e) => {
        handlePickMove(e.latLng.lat(), e.latLng.lng());
      });
    } else {
      markerRef.current.setPosition({ lat, lng });
    }

    if (!circleRef.current) {
      circleRef.current = new google.maps.Circle({
        map,
        center: { lat, lng },
        radius,
        strokeColor: "#6c63ff",
        strokeWeight: 1,
        fillColor: "#6c63ff",
        fillOpacity: 0.15,
      });
    } else {
      circleRef.current.setCenter({ lat, lng });
      circleRef.current.setRadius(radius);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePickMove = useCallback(
    async (lat, lng) => {
      if (!geocoderRef.current) return;
      const geo = await reverseGeocode(geocoderRef.current, { lat, lng });
      const radius = valueRef.current?.radius || DEFAULT_LOCATION_RADIUS;
      setMarkerAndCircle(window.google, mapRef.current, lat, lng, radius);
      onChangeRef.current?.({ latitude: lat, longitude: lng, radius, ...geo });
      if (searchInputRef.current) searchInputRef.current.value = geo.address;
    },
    [setMarkerAndCircle]
  );

  useEffect(() => {
    if (mode !== "pick" || status !== "ready") return;
    const google = window.google;
    const map = mapRef.current;

    map.addListener("click", (e) => handlePickMove(e.latLng.lat(), e.latLng.lng()));

    if (searchInputRef.current) {
      autocompleteRef.current = new google.maps.places.Autocomplete(searchInputRef.current, {
        fields: ["geometry", "formatted_address", "address_components"],
      });
      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        const loc = place?.geometry?.location;
        if (!loc) return;
        const lat = loc.lat();
        const lng = loc.lng();
        const components = place.address_components || [];
        const find = (type) => components.find((c) => c.types.includes(type))?.long_name || "";
        const geo = {
          address: place.formatted_address || "",
          city: find("locality") || find("postal_town") || "",
          country: find("country") || "",
        };
        const radius = valueRef.current?.radius || DEFAULT_LOCATION_RADIUS;
        setMarkerAndCircle(google, map, lat, lng, radius);
        map.panTo({ lat, lng });
        map.setZoom(16);
        onChangeRef.current?.({ latitude: lat, longitude: lng, radius, ...geo });
      });
    }

    if (value?.latitude && value?.longitude) {
      setMarkerAndCircle(google, map, value.latitude, value.longitude, value.radius || DEFAULT_LOCATION_RADIUS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, status]);

  const adjustPickRadius = (delta) => {
    if (!value?.latitude) return;
    const nextRadius = Math.max(MIN_RADIUS, Math.round((value.radius || DEFAULT_LOCATION_RADIUS) + delta));
    if (circleRef.current) circleRef.current.setRadius(nextRadius);
    applyPickValue({ ...value, radius: nextRadius });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t("locationMapPicker.toasts.geoNotAvailable"));
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setIsLocating(false);
        await handlePickMove(pos.coords.latitude, pos.coords.longitude);
        mapRef.current?.panTo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        mapRef.current?.setZoom(16);
      },
      () => {
        setIsLocating(false);
        toast.error(t("locationMapPicker.toasts.locateFailed"));
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // =========================================================================
  // MODE: checkIn
  // =========================================================================
  const checkInPoints = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const syncCheckInOverlays = useCallback(() => {
    const google = window.google;
    const map = mapRef.current;
    if (!google || !map) return;

    const seen = new Set();
    checkInPoints.forEach((point, index) => {
      seen.add(point.id);
      let overlay = pointOverlaysRef.current.get(point.id);
      if (!overlay) {
        const marker = new google.maps.Marker({
          map,
          position: { lat: point.latitude, lng: point.longitude },
          draggable: true,
          label: String(index + 1),
        });
        const circle = new google.maps.Circle({
          map,
          center: { lat: point.latitude, lng: point.longitude },
          radius: point.radius || DEFAULT_CHECKIN_RADIUS,
          strokeColor: "#f59e0b",
          strokeWeight: 1,
          fillColor: "#f59e0b",
          fillOpacity: 0.15,
        });
        marker.addListener("dragend", (e) => {
          updateCheckInPoint(point.id, { latitude: e.latLng.lat(), longitude: e.latLng.lng() });
        });
        overlay = { marker, circle };
        pointOverlaysRef.current.set(point.id, overlay);
      } else {
        overlay.marker.setPosition({ lat: point.latitude, lng: point.longitude });
        overlay.marker.setLabel(String(index + 1));
        overlay.circle.setCenter({ lat: point.latitude, lng: point.longitude });
        overlay.circle.setRadius(point.radius || DEFAULT_CHECKIN_RADIUS);
      }
    });

    // remove overlays for points no longer present
    for (const [id, overlay] of pointOverlaysRef.current.entries()) {
      if (!seen.has(id)) {
        overlay.marker.setMap(null);
        overlay.circle.setMap(null);
        pointOverlaysRef.current.delete(id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInPoints]);

  useEffect(() => {
    if (mode !== "checkIn" || status !== "ready") return;
    const google = window.google;
    const map = mapRef.current;

    // main location marker for context
    new google.maps.Marker({
      map,
      position: { lat: effectiveCenter.latitude, lng: effectiveCenter.longitude },
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: "#6c63ff",
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: 2,
      },
      title: t("locationMapPicker.eventLocationMarker"),
      clickable: false,
    });

    syncCheckInOverlays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, status]);

  useEffect(() => {
    if (mode !== "checkIn" || status !== "ready") return;
    syncCheckInOverlays();
  }, [mode, status, syncCheckInOverlays]);

  // Reads via refs (not the `checkInPoints`/`onChange` closure) so the dragend
  // listener attached once per marker in syncCheckInOverlays never acts on a
  // stale points array — see the valueRef/onChangeRef comment above.
  const updateCheckInPoint = (id, changes) => {
    const current = Array.isArray(valueRef.current) ? valueRef.current : [];
    onChangeRef.current?.(current.map((p) => (p.id === id ? { ...p, ...changes } : p)));
  };

  const addCheckInPoint = () => {
    const map = mapRef.current;
    const centerPos = map ? map.getCenter() : null;
    const lat = centerPos ? centerPos.lat() : effectiveCenter.latitude;
    const lng = centerPos ? centerPos.lng() : effectiveCenter.longitude;
    const newPoint = {
      id: nextCheckInId(),
      name: t("locationMapPicker.gateDefaultName", { index: checkInPoints.length + 1 }),
      latitude: lat,
      longitude: lng,
      radius: DEFAULT_CHECKIN_RADIUS,
    };
    onChange?.([...checkInPoints, newPoint]);
  };

  const removeCheckInPoint = (id) => {
    onChange?.(checkInPoints.filter((p) => p.id !== id));
  };

  // =========================================================================
  // MODE: zone
  // =========================================================================
  const syncZoneOverlays = useCallback((points) => {
    const google = window.google;
    const map = mapRef.current;
    if (!google || !map) return;

    zoneMarkersRef.current.forEach((m) => m.setMap(null));
    zoneMarkersRef.current = points.map(
      (point, index) =>
        new google.maps.Marker({
          map,
          position: { lat: point.latitude, lng: point.longitude },
          label: String(index + 1),
        })
    );

    if (zonePolygonRef.current) zonePolygonRef.current.setMap(null);
    if (points.length >= 2) {
      zonePolygonRef.current = new google.maps.Polygon({
        map,
        paths: points.map((p) => ({ lat: p.latitude, lng: p.longitude })),
        strokeColor: "#6c63ff",
        strokeWeight: 2,
        fillColor: "#6c63ff",
        fillOpacity: 0.2,
      });
    }
  }, []);

  useEffect(() => {
    if (mode !== "zone" || status !== "ready") return;
    const google = window.google;
    const map = mapRef.current;

    new google.maps.Marker({
      map,
      position: { lat: effectiveCenter.latitude, lng: effectiveCenter.longitude },
      title: t("locationMapPicker.eventLocationMarker"),
      clickable: false,
    });

    map.addListener("click", (e) => {
      const coordinate = { latitude: e.latLng.lat(), longitude: e.latLng.lng() };
      const current = zonePointsRef.current;
      if (!canAddEventZonePoint(current)) {
        toast.warning(t("locationMapPicker.toasts.maxZonePoints", { max: MAX_EVENT_ZONE_POINTS }));
        return;
      }
      if (isDuplicatePoint(coordinate, current)) {
        toast.warning(t("locationMapPicker.toasts.pointTooClose"));
        return;
      }
      const next = [...current, { ...coordinate, order: current.length + 1 }];
      setZonePoints(next);
      onChange?.(next);
    });

    syncZoneOverlays(zonePointsRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, status]);

  useEffect(() => {
    if (mode !== "zone") return;
    syncZoneOverlays(zonePoints);
  }, [mode, zonePoints, syncZoneOverlays]);

  const undoZonePoint = () => {
    const next = zonePoints.slice(0, -1).map((p, i) => ({ ...p, order: i + 1 }));
    setZonePoints(next);
    onChange?.(next);
  };

  const clearZone = () => {
    setZonePoints([]);
    onChange?.([]);
  };

  const zoneSelfIntersects = doesPolygonSelfIntersect(zonePoints);

  // =========================================================================
  // fallback (no key configured / script failed)
  // =========================================================================
  if (status === "unconfigured" || status === "error") {
    return (
      <div style={s.fallback}>
        <p style={s.fallbackTitle}>
          {status === "unconfigured" ? t("locationMapPicker.mapNotConfigured") : t("locationMapPicker.mapFailedToLoad")}
        </p>
        <p style={s.fallbackText}>
          {status === "unconfigured"
            ? t("locationMapPicker.unconfiguredHint")
            : t("locationMapPicker.loadErrorHint")}
        </p>
        {mode === "pick" && (
          <ManualPickFallback value={value} onChange={onChange} />
        )}
        {mode === "checkIn" && (
          <ManualCheckInFallback points={checkInPoints} onChange={onChange} center={effectiveCenter} />
        )}
        {mode === "zone" && (
          <p style={s.fallbackText}>{t("locationMapPicker.zoneRequiresMapHint")}</p>
        )}
      </div>
    );
  }

  return (
    <div>
      {mode === "pick" && (
        <div style={s.searchRow}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t("locationMapPicker.searchPlaceholder")}
            defaultValue={value?.address || ""}
            style={s.searchInput}
          />
          <button type="button" style={s.locateBtn} onClick={useMyLocation} disabled={isLocating}>
            {isLocating ? "…" : <><MapPin size={13} strokeWidth={2.25} /> {t("locationMapPicker.myLocation")}</>}
          </button>
        </div>
      )}

      {mode === "checkIn" && (
        <div style={s.toolbar}>
          <button type="button" style={s.toolBtn} onClick={addCheckInPoint}>
            {t("locationMapPicker.addCheckInPin")}
          </button>
          <span style={s.toolHint}>{t("locationMapPicker.checkInHint")}</span>
        </div>
      )}

      {mode === "zone" && (
        <div style={s.toolbar}>
          <button type="button" style={s.toolBtn} onClick={undoZonePoint} disabled={!zonePoints.length}>
            {t("locationMapPicker.undoPoint")}
          </button>
          <button type="button" style={s.toolBtnDanger} onClick={clearZone} disabled={!zonePoints.length}>
            {t("locationMapPicker.clearZone")}
          </button>
          <span style={s.toolHint}>{t("locationMapPicker.zoneHint", { count: zonePoints.length, max: MAX_EVENT_ZONE_POINTS })}</span>
        </div>
      )}

      <div ref={containerRef} style={{ width: "100%", height, borderRadius: 10, overflow: "hidden", background: "#eee" }} />

      {status === "loading" && <p style={s.loadingText}>{t("locationMapPicker.loadingMap")}</p>}

      {mode === "pick" && value?.latitude != null && (
        <div style={s.radiusRow}>
          <span>{t("locationMapPicker.radiusLabel")}</span>
          <button type="button" style={s.stepBtn} onClick={() => adjustPickRadius(-RADIUS_STEP)}>−</button>
          <strong>{value.radius || DEFAULT_LOCATION_RADIUS}m</strong>
          <button type="button" style={s.stepBtn} onClick={() => adjustPickRadius(RADIUS_STEP)}>+</button>
        </div>
      )}

      {mode === "pick" && value?.address && <p style={s.addressText}>{value.address}</p>}

      {mode === "checkIn" && checkInPoints.length > 0 && (
        <div style={s.pointList}>
          {checkInPoints.map((point, index) => (
            <div key={point.id} style={s.pointRow}>
              <span style={s.pointIndex}>{index + 1}</span>
              <input
                type="text"
                value={point.name}
                onChange={(e) => updateCheckInPoint(point.id, { name: e.target.value })}
                style={s.pointNameInput}
                maxLength={40}
              />
              <button type="button" style={s.stepBtn} onClick={() => updateCheckInPoint(point.id, { radius: Math.max(MIN_RADIUS, (point.radius || DEFAULT_CHECKIN_RADIUS) - RADIUS_STEP) })}>−</button>
              <span style={s.pointRadius}>{point.radius || DEFAULT_CHECKIN_RADIUS}m</span>
              <button type="button" style={s.stepBtn} onClick={() => updateCheckInPoint(point.id, { radius: (point.radius || DEFAULT_CHECKIN_RADIUS) + RADIUS_STEP })}>+</button>
              <button type="button" style={s.removePointBtn} onClick={() => removeCheckInPoint(point.id)}><X size={11} strokeWidth={2.25} /></button>
            </div>
          ))}
        </div>
      )}

      {mode === "zone" && zoneSelfIntersects && (
        <p style={s.errorText}>{t("locationMapPicker.zoneSelfIntersectError")}</p>
      )}
    </div>
  );
};

const ManualPickFallback = ({ value, onChange }) => {
  const { t } = useTranslation("events");
  const set = (key, val) => onChange?.({ ...(value || {}), [key]: val });
  return (
    <div style={s.manualGrid}>
      <input style={s.manualInput} placeholder={t("locationMapPicker.manual.address")} value={value?.address || ""} onChange={(e) => set("address", e.target.value)} />
      <input style={s.manualInput} placeholder={t("locationMapPicker.manual.city")} value={value?.city || ""} onChange={(e) => set("city", e.target.value)} />
      <input style={s.manualInput} placeholder={t("locationMapPicker.manual.country")} value={value?.country || ""} onChange={(e) => set("country", e.target.value)} />
      <input style={s.manualInput} placeholder={t("locationMapPicker.manual.latitude")} type="number" value={value?.latitude ?? ""} onChange={(e) => set("latitude", Number(e.target.value))} />
      <input style={s.manualInput} placeholder={t("locationMapPicker.manual.longitude")} type="number" value={value?.longitude ?? ""} onChange={(e) => set("longitude", Number(e.target.value))} />
      <input style={s.manualInput} placeholder={t("locationMapPicker.manual.radius")} type="number" value={value?.radius ?? DEFAULT_LOCATION_RADIUS} onChange={(e) => set("radius", Number(e.target.value))} />
    </div>
  );
};

const ManualCheckInFallback = ({ points, onChange, center }) => {
  const { t } = useTranslation("events");
  const addPoint = () => {
    onChange?.([
      ...points,
      { id: nextCheckInId(), name: t("locationMapPicker.gateDefaultName", { index: points.length + 1 }), latitude: center.latitude, longitude: center.longitude, radius: DEFAULT_CHECKIN_RADIUS },
    ]);
  };
  const update = (id, changes) => onChange?.(points.map((p) => (p.id === id ? { ...p, ...changes } : p)));
  const remove = (id) => onChange?.(points.filter((p) => p.id !== id));

  return (
    <div style={s.pointList}>
      {points.map((point, index) => (
        <div key={point.id} style={s.pointRow}>
          <span style={s.pointIndex}>{index + 1}</span>
          <input style={s.pointNameInput} value={point.name} onChange={(e) => update(point.id, { name: e.target.value })} />
          <input style={{ ...s.manualInput, width: 90 }} type="number" placeholder={t("locationMapPicker.manual.lat")} value={point.latitude} onChange={(e) => update(point.id, { latitude: Number(e.target.value) })} />
          <input style={{ ...s.manualInput, width: 90 }} type="number" placeholder={t("locationMapPicker.manual.lng")} value={point.longitude} onChange={(e) => update(point.id, { longitude: Number(e.target.value) })} />
          <button type="button" style={s.removePointBtn} onClick={() => remove(point.id)}><X size={11} strokeWidth={2.25} /></button>
        </div>
      ))}
      <button type="button" style={s.toolBtn} onClick={addPoint}>{t("locationMapPicker.addCheckInPin")}</button>
    </div>
  );
};

const s = {
  searchRow: { display: "flex", gap: 8, marginBottom: 8 },
  searchInput: {
    flex: 1, padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, outline: "none",
  },
  locateBtn: {
    padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, background: "#fff", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
  },
  toolbar: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" },
  toolBtn: {
    padding: "7px 12px", border: "1.5px solid #6c63ff", borderRadius: 8, background: "#fff", color: "#6c63ff", fontSize: 12, fontWeight: 600, cursor: "pointer",
  },
  toolBtnDanger: {
    padding: "7px 12px", border: "1.5px solid #e53935", borderRadius: 8, background: "#fff", color: "#e53935", fontSize: 12, fontWeight: 600, cursor: "pointer",
  },
  toolHint: { fontSize: 11, color: "#888" },
  loadingText: { fontSize: 12, color: "#888", marginTop: 6 },
  radiusRow: { display: "flex", alignItems: "center", gap: 10, marginTop: 10, fontSize: 13, color: "#444" },
  stepBtn: {
    width: 26, height: 26, borderRadius: "50%", border: "1.5px solid #d8dbe3", background: "#fff", cursor: "pointer", fontSize: 14, lineHeight: 1,
  },
  addressText: { fontSize: 12, color: "#666", marginTop: 8 },
  pointList: { marginTop: 10, display: "flex", flexDirection: "column", gap: 8 },
  pointRow: { display: "flex", alignItems: "center", gap: 8 },
  pointIndex: {
    width: 20, height: 20, borderRadius: "50%", background: "#fde68a", color: "#92400e", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  pointNameInput: {
    flex: 1, padding: "6px 10px", border: "1.5px solid #e5e7eb", borderRadius: 6, fontSize: 12, outline: "none",
  },
  pointRadius: { fontSize: 12, color: "#444", minWidth: 34, textAlign: "center" },
  removePointBtn: {
    width: 22, height: 22, borderRadius: "50%", border: "none", background: "#fee2e2", color: "#e53935", fontSize: 11, cursor: "pointer",
  },
  errorText: { fontSize: 12, color: "#e53935", marginTop: 8 },
  fallback: { padding: 16, border: "1.5px dashed #e5e7eb", borderRadius: 10, background: "#fafafa" },
  fallbackTitle: { margin: "0 0 4px", fontWeight: 700, fontSize: 13, color: "#444" },
  fallbackText: { margin: "0 0 12px", fontSize: 12, color: "#888" },
  manualGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  manualInput: {
    padding: "8px 10px", border: "1.5px solid #e5e7eb", borderRadius: 6, fontSize: 12, outline: "none",
  },
};

export default LocationMapPicker;
